import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { withWorkspaceFilter, getActiveWorkspaceId } from "@/utils/workspace-helpers";

export interface FinagoIntegration {
  id: string;
  organization_name: string | null;
  organization_number: string | null;
  organization_id: string;
  is_active: boolean;
  created_at: string;
  token_expires_at: string | null;
}

export function useFinagoIntegration() {
  const useHasActiveFinagoQuery = () => {
    return useQuery({
      queryKey: ["has-active-finago", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;

        const { data, error } = await withWorkspaceFilter(
          supabase.from("finago_integrations")
          .select("id")
          .eq("is_active", true)
          .limit(1)
        );

        if (error) {
          throw error;
        }

        return data && data.length > 0;
      },
    });
  };

  const useLastSyncTimeQuery = () => {
    return useQuery({
      queryKey: ["finago-last-sync", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        const { data, error } = await withWorkspaceFilter(
          supabase.from("finago_detection_runs")
          .select("run_at")
          .order("run_at", { ascending: false })
          .limit(1)
          .single()
        );

        if (error) {
          if (error.code === "PGRST116") {
            return null;
          }
          throw error;
        }

        return data?.run_at ? new Date(data.run_at) : null;
      },
      refetchInterval: 60000, // Refetch every minute
    });
  };

  const useFinagoIntegrationsQuery = () => {
    return useQuery({
      queryKey: ["finago-integrations", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        const { data, error } = await withWorkspaceFilter(
          supabase.from("finago_integrations")
          .select("*")
          .order("created_at", { ascending: false })
        );

        if (error) throw error;
        return (data || []) as FinagoIntegration[];
      },
    });
  };

  const useActiveFinagoIntegrationsQuery = () => {
    return useQuery({
      queryKey: ["finago-integrations-active", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        const { data, error } = await withWorkspaceFilter(
          supabase.from("finago_integrations")
          .select("*")
          .eq("is_active", true)
          .order("created_at", { ascending: false })
        );

        if (error) throw error;
        return (data || []) as FinagoIntegration[];
      },
    });
  };

  return {
    useHasActiveFinagoQuery,
    useLastSyncTimeQuery,
    useFinagoIntegrationsQuery,
    useActiveFinagoIntegrationsQuery,
  };
}
